import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { values, mapValues, get, pickBy, omit } from 'lodash';

import ButtonWithLoading from '../components/ButtonWithLoading';
import { emailRegex, phoneRegex } from '../constants/regex';

const COUPON_STATUS = {
  DEFAULT: 'DEFAULT',
  SUCCESS: 'SUCCESS',
  ERROR: 'ERROR',
};

const VALIDATION_RULES = {
  nom_prenom: {
    fn: ({ nom_prenom }) => nom_prenom.length > 5,
    message: "Doit faire plus de 5 lettres",
  },
  adresse: {
    fn: ({ adresse }) => adresse.length > 10,
    message: "Doit faire plus de 10 lettres",
  },
  code_postal: {
    fn: ({ code_postal }) => code_postal.length >= 4 && code_postal.length <= 7,
    message: "Doit être constitué de 4 à 7 chiffres",
  },
  ville: {
    fn: ({ ville }) => ville.length > 3,
    message: "Doit faire plus de 3 lettres",
  },
  email: {
    fn: ({ email }) => !!email.match(emailRegex),
    message: "Doit être un email valide",
  },
  telephone: {
    fn: ({ telephone }) => !!telephone.match(phoneRegex),
    message: "Doit être un numéro de téléphone valide",
  },
  nom_prenom_sautant: {
    fn: ({ mode_achat, nom_prenom_sautant }) => mode_achat === 'optionPersonal' || nom_prenom_sautant.length > 5,
    message: "Doit faire plus de 5 lettres",
  },
  email_sautant: {
    fn: ({ mode_achat, email_sautant }) => mode_achat === 'optionPersonal' || !!email_sautant.match(emailRegex),
    message: "Doit être un email valide",
  },
};

class ClientDetails extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      form: {
        nom_prenom: '',
        adresse: '',
        code_postal: '',
        ville: '',
        email: '',
        telephone: '',
        nom_prenom_sautant: '',
        email_sautant: '',
        cadeau_message: '',
        mode_achat: 'optionPersonal',
      },
      errors: {},
      coupon: null,
      coupon_code: '',
      coupon_status: COUPON_STATUS.DEFAULT,
      fetching: {
        coupon: false,
        order: false,
      },
    };
  }

  validateAll = () => {
    const { form } = this.state;

    // Compute errors on all fields
    const errors = mapValues(form, (value, name) => {
      const validator = get(VALIDATION_RULES, [name, 'fn']);
      
      // if there is a validation function, and the field is invalid, return the error message
      if(validator && !validator(form)) {
        return get(VALIDATION_RULES, [name, 'message']);
      }

      return null;
    });

    this.setState({
      errors: pickBy(errors, msg => msg !== null),
    });

    return values(errors).every(a => !a);
  };

  clearErrorFor = (name) => {
    const { errors } = this.state;
    
    this.setState({
      errors: omit(errors, name),
    });
  }

  onChange = (event) => {
    const { target: { name, value }} = event;

    // Reset coupon state if edited
    if (name === 'coupon_code') {
      this.setState({
        coupon_status: COUPON_STATUS.DEFAULT,
      });
    }

    // Update state in the right place
    if (this.state.form.hasOwnProperty(name)) {
      this.setState({
        form: {
          ...this.state.form,
          [name]: value,
        },
      }, () => this.clearErrorFor(name));
    } else {
      this.setState({
        [name]: value,
      });
    }
  }

  setFetching = (key, isFetching) => {
    this.setState({
      fetching: {
        ...this.state.fetching,
        [key]: isFetching,
      },
    });
  }

  onCouponApply = () => {
    const { coupon_code } = this.state;
    const { product } = this.props;

    this.setFetching('coupon', true);

    fetch(`/api/coupon/${coupon_code}`)
      .then(res => {
        if (res.status === 200) {
          return res.json();
        } else {
          throw new Error(res.statusText);
        }
      })
      .then(data => {
        // If the coupon is restricted to a product and it's not this one
        if (data.only_product_id && data.only_product_id !== product.id) {
          throw "Coupon is not available on this product";
        }

        this.setState({
          coupon: data,
          coupon_status: COUPON_STATUS.SUCCESS,
        });

        this.setFetching('coupon', false);
      })
      .catch(err => {
        this.setState({
          coupon: null,
          coupon_status: COUPON_STATUS.ERROR,
        });

        this.setFetching('coupon', false);
      });
  }

  handleSubmit = () => {
    if (this.validateAll()) {
      this.setFetching('order', true);

      this.props.onSubmit({
        details: this.state.form,
        coupon_code: this.state.coupon_code,
      });
    }
  }

  computeTotal = (product, option, coupon) => {
    let total = product.price_cents;

    if (option) {
      total += option.price_cents;
    }

    if (coupon) {
      if (coupon.type === 'pourcentage') {
        total *= (100 - coupon.reduction) / 100;
      } else {
        total -= coupon.reduction;
      }
    }

    return total / 100;
  }

  render() {
    const {
      form: { nom_prenom,
        adresse,
        code_postal,
        ville,
        email,
        telephone,
        nom_prenom_sautant,
        email_sautant,
        cadeau_message,
        mode_achat,
      },
      errors,
      coupon,
      coupon_code,
      coupon_status,
      fetching,
    } = this.state;
    const { product, option } = this.props;

    const isGift = mode_achat === 'optionGift';
    const total = this.computeTotal(product, option, coupon);

    return (
      <div>
        <ul className="text-center tunnel_steps">
          <li className="tunnel_steps_step active">
            <span><i className="fa fa-check"></i></span>
          </li>
          <li className="tunnel_steps_step active">
            <span>2</span>
          </li>
          <li className="tunnel_steps_step">
            <span>3</span>
          </li>
          <li className="tunnel_steps_step">
            <span>4</span>
          </li>
        </ul>

        {/* PANIER */}
        <div className="clearfix">
          <h2 className="col-xs-12">Vos coordonnées</h2>
        </div>
        <div className="col-sm-7 tunnel_main">
          <div className="tunnel_gift">
            <div className="sm-container custom-radio custom-radio--lg">
              <input
                type="radio"
                name="mode_achat"
                value="optionPersonal"
                id="optionPersonal"
                checked={!isGift}
                onChange={this.onChange}
              />
              <label htmlFor="optionPersonal"><span></span>Ceci est pour moi</label>
            </div>
            <div className="sm-container custom-radio custom-radio--lg custom-radio--primary">
              <input
                type="radio"
                name="mode_achat"
                value="optionGift"
                id="optionGift"
                checked={isGift}
                onChange={this.onChange}
              />
              <label htmlFor="optionGift"><span></span><i className="fa fa-gift"></i> Ceci est un cadeau</label>
            </div>
          </div>
          <div className="sm-container pd0 tunnel_form">
            {
              isGift &&
                <div id="formGift">
                  <h4 className="tunnel_side_title">Les coordonnées du destinataire</h4>
                  <div className={cx('group', errors.nom_prenom_sautant && 'error')}>
                    <input
                      type="text"
                      name="nom_prenom_sautant"
                      value={nom_prenom_sautant}
                      onChange={this.onChange}
                    />
                    <span className="highlight"></span>
                    <span className="bar"></span>
                    <label>Nom, Prénom</label>
                    {errors.nom_prenom_sautant && 
                      <div className="tunnel_error_message">
                        {errors.nom_prenom_sautant}
                      </div>
                    }
                  </div>

                  <div className={cx('group', errors.email_sautant && 'error')}>
                    <input
                      type="text"
                      name="email_sautant"
                      value={email_sautant}
                      onChange={this.onChange}
                    />
                    <span className="highlight"></span>
                    <span className="bar"></span>
                    <label>Email</label>
                    {errors.email_sautant && 
                      <div className="tunnel_error_message">
                        {errors.email_sautant}
                      </div>
                    }
                  </div>

                  <div className="tunnel_form_textarea">
                    <label>Message</label>
                    <textarea
                      name="cadeau_message"
                      placeholder="Un petit mot pour votre destinataire ? (les émojis ne sont pas supportés)"
                      rows="4"
                      value={cadeau_message}
                      onChange={this.onChange}
                    />
                    {errors.cadeau_message && 
                      <div className="tunnel_error_message">
                        {errors.cadeau_message}
                      </div>
                    }
                  </div>
                </div>
            }
            <h4 className="tunnel_side_title">Vos informations personnelles</h4>
            <div className={cx('group', errors.nom_prenom && 'error')}>
              <input
                type="text"
                name="nom_prenom"
                value={nom_prenom}
                onChange={this.onChange}
              />
              <span className="highlight"></span>
              <span className="bar"></span>
              <label>Nom, Prénom</label>
              {errors.nom_prenom && 
                <div className="tunnel_error_message">
                  {errors.nom_prenom}
                </div>
              }
            </div>

            <div className={cx('group', errors.adresse && 'error')}>
              <input
                type="text"
                name="adresse"
                value={adresse}
                onChange={this.onChange}
              />
              <span className="highlight"></span>
              <span className="bar"></span>
              <label>Adresse</label>
              {errors.adresse && 
                <div className="tunnel_error_message">
                  {errors.adresse}
                </div>
              }
            </div>

            <div className={cx('group', errors.code_postal && 'error')}>
              <input
                type="text"
                name="code_postal"
                value={code_postal}
                onChange={this.onChange}
              />
              <span className="highlight"></span>
              <span className="bar"></span>
              <label>Code postal</label>
              {errors.code_postal && 
                <div className="tunnel_error_message">
                  {errors.code_postal}
                </div>
              }
            </div>
            
            <div className={cx('group', errors.ville && 'error')}>
              <input
                type="text"
                name="ville"
                value={ville}
                onChange={this.onChange}
              />
              <span className="highlight"></span>
              <span className="bar"></span>
              <label>Ville</label>
              {errors.ville && 
                <div className="tunnel_error_message">
                  {errors.ville}
                </div>
              }
            </div>

            <div className={cx('group', errors.email && 'error')}>
              <input
                type="email"
                name="email"
                value={email}
                onChange={this.onChange}
              />
              <span className="highlight"></span>
              <span className="bar"></span>
              <label>Email</label>
              {errors.email && 
                <div className="tunnel_error_message">
                  {errors.email}
                </div>
              }
            </div>
            
            <div className={cx('group', errors.telephone && 'error')}>
              <input
                type="text"
                name="telephone"
                value={telephone}
                onChange={this.onChange}
              />
              <span className="highlight"></span>
              <span className="bar"></span>
              <label>Téléphone</label>
              {errors.telephone && 
                <div className="tunnel_error_message">
                  {errors.telephone}
                </div>
              }
            </div>
          </div>
        </div>
        <div className="col-md-4 col-md-offset-1 col-sm-5 tunnel_side">
          <div className="sm-container">
            <h4 className="tunnel_side_title">Résumé de votre commande</h4>
            <p>Vous allez être redirigé vers <b className="bold">notre page sécurisée</b> afin de procéder au paiement.</p>
            <ul className="tunnel_side_items">
              <li className="clearfix">
                <p className="col-xs-9 text-right">{product.description}</p>
                <p className="col-xs-3 text-right product-price">{product.price_cents / 100} €</p>
              </li>
              <li className="clearfix">
                <p className="col-xs-9 text-right option-text">{option ? option.description : 'Aucune option'}</p>
                <p className="col-xs-3 text-right option-price">{option ? `${option.price_cents / 100} €` : 'N/C'}</p>
              </li>
              {
                coupon &&
                  <li className="clearfix">
                    <p className="col-xs-9 text-right">Code promo</p>
                    <p className="col-xs-3 text-right coupon-display">
                      {
                        coupon.type === 'fixe'
                          ?`-${coupon.reduction / 100} €`
                          : `-${coupon.reduction}%`
                      }
                    </p>
                  </li>
              }
            </ul>
            <div className="tunnel_side_final">
              <p className="tunnel_side_final_total">TOTAL <span className="bold">{total} €</span></p>

              <div className="form-group coupon-box">
                <label className="control-label">Appliquer un code promo:</label>
                <div
                  className={cx(
                    'coupon-input-with-button',
                    coupon_status === COUPON_STATUS.ERROR && 'has-error',
                    coupon_status === COUPON_STATUS.SUCCESS && 'has-success',
                  )}
                >
                  <input
                    type="text"
                    id="coupon-code"
                    name="coupon_code"
                    placeholder="COUPON"
                    className="form-control"
                    value={coupon_code}
                    onChange={this.onChange}
                  />
                  <ButtonWithLoading onClick={this.onCouponApply} isLoading={fetching.coupon} className="btn btn-default">
                    Appliquer
                  </ButtonWithLoading>
                </div>
              </div>

              <p>En cliquant sur ce bouton, vous acceptez les <a href="/images/cgv.pdf" target="_blank">conditions générales de ventes.</a></p>

              <ButtonWithLoading onClick={this.handleSubmit} isLoading={fetching.order} className="btn btn-primary">
                <i className="fa fa-lock"></i> Procéder au paiement <i className="fa fa-chevron-right"></i>
              </ButtonWithLoading>

              {/* <!-- <a href="#" className="btn btn-primary tunnel-paiement-button" onclick="document.getElementById('form-infos').submit();"><i className="fa fa-lock"></i> Procéder au paiement <i className="fa fa-chevron-right"></i> </a> --> */}
            </div>
          </div>
          {/* <!-- <a href="#" className="tunnel_side_back"><i className="fa fa-chevron-left"></i>Modifier mon panier</a> --> */}
        </div>
      </div>
    );
  }
}

ClientDetails.propTypes = {
  product: PropTypes.object.isRequired,
  option: PropTypes.object,
  errors: PropTypes.object.isRequired,
  onSubmit: PropTypes.func.isRequired,
};

export default ClientDetails;
